/**
 * @file Billing API client — wraps data-api `/billing/*` endpoints.
 *
 * All endpoints except `/billing/packs` require an authenticated user. The
 * caller is responsible for passing the `token` (from `session?.user?.accessToken`).
 *
 * The data-api side is in PR https://github.com/Hoops-Finance/data-api/pull/23
 * (closes #21 + #22). Schema + behavior documented in
 * `data-api/docs/billing.md`.
 */

const API_BASE = process.env.NEXT_PUBLIC_BASE_DATA_URI ?? "https://api.hoops.finance";

// ── Types ───────────────────────────────────────────────────────────────────

export interface CreditPack {
  id: string;
  credits: number;
  displayName: string;
}

export interface BalanceSummary {
  balance: number;
  totalGranted: number;
  totalSpent: number;
  lastTopUp: string | null; // ISO date string
}

export type LedgerReason =
  | "ai_query"
  | "stripe_purchase"
  | "promo_redeem"
  | "admin_grant"
  | "refund";

export interface LedgerEntry {
  _id: string;
  userId: string;
  delta: number;
  balanceAfter: number;
  reason: LedgerReason;
  metadata?: {
    stripeSessionId?: string;
    stripeEventId?: string;
    promoCode?: string;
    queryNote?: string;
    grantedBy?: string;
  };
  ts: string;
}

export interface LedgerPage {
  entries: LedgerEntry[];
  hasMore: boolean;
}

export interface RedeemResult {
  ok: true;
  granted: number;
  balance: number;
  alreadyApplied: boolean;
}

/**
 * Error thrown by billing service calls. Subclasses Error so it satisfies
 * lint rules (only-throw-error) while preserving the `{ error, status }`
 * shape callers already destructure for branching on status code.
 */
export class BillingError extends Error {
  public readonly status: number;
  public readonly error: string;
  constructor(status: number, message: string) {
    super(message);
    this.name = "BillingError";
    this.status = status;
    // Also expose `error` as a public property so existing call-sites that
    // do `(err as { error?: string }).error` keep working.
    this.error = message;
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function authHeaders(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (res.ok) return (await res.json()) as T;
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = { error: res.statusText };
  }
  const message =
    typeof body === "object" && body !== null && "error" in body
      ? String((body as { error: unknown }).error)
      : `Request failed (${String(res.status)})`;
  throw new BillingError(res.status, message);
}

// ── Public ──────────────────────────────────────────────────────────────────

export async function fetchPacks(): Promise<CreditPack[]> {
  const res = await fetch(`${API_BASE}/billing/packs`, { signal: AbortSignal.timeout(10_000) });
  const body = await jsonOrThrow<{ packs: CreditPack[] }>(res);
  return body.packs;
}

// ── Auth required ───────────────────────────────────────────────────────────

export async function fetchBalance(token: string): Promise<BalanceSummary> {
  const res = await fetch(`${API_BASE}/billing/balance`, {
    headers: authHeaders(token),
    signal: AbortSignal.timeout(10_000),
  });
  return jsonOrThrow<BalanceSummary>(res);
}

export async function fetchHistory(
  token: string,
  opts?: { limit?: number; before?: string }
): Promise<LedgerPage> {
  const params = new URLSearchParams();
  if (opts?.limit) params.set("limit", String(opts.limit));
  if (opts?.before) params.set("before", opts.before);
  const url = params.toString()
    ? `${API_BASE}/billing/history?${params.toString()}`
    : `${API_BASE}/billing/history`;
  const res = await fetch(url, {
    headers: authHeaders(token),
    signal: AbortSignal.timeout(10_000),
  });
  return jsonOrThrow<LedgerPage>(res);
}

export async function startCheckout(token: string, packId: string): Promise<{ sessionUrl: string }> {
  const res = await fetch(`${API_BASE}/billing/checkout`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ packId }),
    signal: AbortSignal.timeout(15_000),
  });
  return jsonOrThrow<{ sessionUrl: string }>(res);
}

export async function redeemCode(token: string, code: string): Promise<RedeemResult> {
  const res = await fetch(`${API_BASE}/billing/redeem`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ code }),
    signal: AbortSignal.timeout(15_000),
  });
  return jsonOrThrow<RedeemResult>(res);
}
